import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Counter,
  Dialog,
  DialogContentContainer,
  Divider,
  Flex,
  Icon,
  Loader,
  Search,
  Text,
  Checkbox,
  Avatar,
} from '@vibe/core';
import {
  Team as TeamIcon,
  Person,
  Board as BoardIcon,
  DropdownChevronDown,
  Close,
} from '@vibe/icons';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { PickedBoard } from '../shared/BoardPicker';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Board search constants ──────────────────────────────────────────────────

const BOARDS_QUERY = `
  query GetBoards($limit: Int!, $page: Int!) {
    boards(limit: $limit, page: $page, order_by: used_at) {
      id
      name
      type
    }
  }
`;

const DEFAULT_LIMIT = 10;
const SEARCH_LIMIT = 100;
const SEARCH_MIN_CHARS = 2;
const MAX_RESULTS = 20;
const DEBOUNCE_MS = 300;

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserOption {
  id: number;
  name: string;
  avatarUrl?: string;
}

interface TeamOption {
  id: number;
  name: string;
  memberNames?: string[];
}

interface TimesheetFilterBarProps {
  userOptions: UserOption[];
  teamOptions: TeamOption[];
  selectedUserIds: number[];
  selectedTeamIds: number[];
  selectedBoards: PickedBoard[];
  onUserSelectionChange: (userIds: number[]) => void;
  onTeamSelectionChange: (teamIds: number[]) => void;
  onBoardSelectionChange: (boards: PickedBoard[]) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const TimesheetFilterBar: React.FC<TimesheetFilterBarProps> = ({
  userOptions,
  teamOptions,
  selectedUserIds,
  selectedTeamIds,
  selectedBoards,
  onUserSelectionChange,
  onTeamSelectionChange,
  onBoardSelectionChange,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const lowerSearch = searchValue.toLowerCase();

  const totalActive = selectedUserIds.length + selectedTeamIds.length + selectedBoards.length;

  // ── Board picker state ──────────────────────────────────────────────────────

  const [boardSearchValue, setBoardSearchValue] = useState('');
  const debouncedBoardQuery = useDebouncedValue(boardSearchValue, DEBOUNCE_MS);
  const [defaultBoards, setDefaultBoards] = useState<PickedBoard[]>([]);
  const [boardResults, setBoardResults] = useState<PickedBoard[]>([]);
  const [isBoardSearching, setIsBoardSearching] = useState(false);
  const [isLoadingDefaultBoards, setIsLoadingDefaultBoards] = useState(false);
  const boardsLoadedRef = useRef(false);

  const selectedBoardIds = useMemo(
    () => new Set(selectedBoards.map((b) => b.id)),
    [selectedBoards],
  );

  // Load default boards on first open
  const loadDefaultBoards = useCallback(async () => {
    if (boardsLoadedRef.current) return;
    boardsLoadedRef.current = true;
    setIsLoadingDefaultBoards(true);
    try {
      const response = await monday.api(BOARDS_QUERY, {
        variables: { limit: DEFAULT_LIMIT, page: 1 },
      });
      const data = response.data as {
        boards?: Array<{ id: string | number; name: string; type?: string }>;
      };
      setDefaultBoards(
        (data.boards ?? [])
          .filter((b) => b.type !== 'sub_items_board')
          .map((b) => ({ id: String(b.id), name: b.name })),
      );
    } catch {
      // Silently fail
    } finally {
      setIsLoadingDefaultBoards(false);
    }
  }, []);

  // Search boards when debounced query changes (2+ characters)
  useEffect(() => {
    if (!debouncedBoardQuery || debouncedBoardQuery.length < SEARCH_MIN_CHARS) {
      setBoardResults([]);
      return;
    }

    let cancelled = false;

    async function search() {
      setIsBoardSearching(true);
      try {
        const response = await monday.api(BOARDS_QUERY, {
          variables: { limit: SEARCH_LIMIT, page: 1 },
        });
        const data = response.data as {
          boards?: Array<{ id: string | number; name: string; type?: string }>;
        };
        if (cancelled) return;

        const lowerQ = debouncedBoardQuery.toLowerCase();
        const matched: PickedBoard[] = [];
        for (const b of data.boards ?? []) {
          if (b.type === 'sub_items_board') continue;
          if (b.name.toLowerCase().includes(lowerQ)) {
            matched.push({ id: String(b.id), name: b.name });
          }
          if (matched.length >= MAX_RESULTS) break;
        }
        setBoardResults(matched);
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setIsBoardSearching(false);
      }
    }

    search();
    return () => { cancelled = true; };
  }, [debouncedBoardQuery]);

  const isBoardSearchMode = debouncedBoardQuery.length >= SEARCH_MIN_CHARS;
  const boardSourceItems = isBoardSearchMode ? boardResults : defaultBoards;
  const boardDisplayItems = boardSourceItems.filter((b) => !selectedBoardIds.has(b.id));

  const toggleBoard = useCallback(
    (board: PickedBoard) => {
      if (selectedBoardIds.has(board.id)) {
        onBoardSelectionChange(selectedBoards.filter((b) => b.id !== board.id));
      } else {
        onBoardSelectionChange([...selectedBoards, board]);
      }
    },
    [selectedBoards, selectedBoardIds, onBoardSelectionChange],
  );

  // ── Toggle handlers ─────────────────────────────────────────────────────────

  const toggleUser = useCallback(
    (id: number) => {
      // When selecting individual users, clear team selection
      if (selectedTeamIds.length > 0) {
        onTeamSelectionChange([]);
      }
      const next = selectedUserIds.includes(id)
        ? selectedUserIds.filter((u) => u !== id)
        : [...selectedUserIds, id];
      onUserSelectionChange(next);
    },
    [selectedUserIds, selectedTeamIds, onUserSelectionChange, onTeamSelectionChange],
  );

  const toggleTeam = useCallback(
    (id: number) => {
      const next = selectedTeamIds.includes(id)
        ? selectedTeamIds.filter((t) => t !== id)
        : [...selectedTeamIds, id];
      onTeamSelectionChange(next);
    },
    [selectedTeamIds, onTeamSelectionChange],
  );

  // ── Search filtering ────────────────────────────────────────────────────────

  const filteredUsers = useMemo(
    () => userOptions.filter((u) => u.name.toLowerCase().includes(lowerSearch)),
    [userOptions, lowerSearch],
  );

  const filteredTeams = useMemo(
    () => teamOptions.filter((t) => t.name.toLowerCase().includes(lowerSearch)),
    [teamOptions, lowerSearch],
  );

  // ── Trigger label ───────────────────────────────────────────────────────────

  const triggerLabel = useMemo(() => {
    if (selectedTeamIds.length > 0) {
      if (selectedTeamIds.length === 1) {
        const team = teamOptions.find((t) => t.id === selectedTeamIds[0]);
        return team?.name ?? '1 team';
      }
      return `${selectedTeamIds.length} teams`;
    }
    if (selectedUserIds.length > 0) {
      if (selectedUserIds.length === 1) {
        const user = userOptions.find((u) => u.id === selectedUserIds[0]);
        return user?.name ?? '1 user';
      }
      return `${selectedUserIds.length} users`;
    }
    return 'Select teams or users';
  }, [selectedTeamIds, selectedUserIds, teamOptions, userOptions]);

  // ── Clear handler ───────────────────────────────────────────────────────────

  const handleClear = useCallback(() => {
    onUserSelectionChange([]);
    onTeamSelectionChange([]);
    onBoardSelectionChange([]);
  }, [onUserSelectionChange, onTeamSelectionChange, onBoardSelectionChange]);

  // ── Chip data ───────────────────────────────────────────────────────────────

  const teamChips = useMemo(() => {
    const lookup = new Map(teamOptions.map((t) => [t.id, t.name]));
    return selectedTeamIds.map((id) => ({
      id,
      label: lookup.get(id) ?? `Team ${id}`,
    }));
  }, [selectedTeamIds, teamOptions]);

  const userChips = useMemo(() => {
    const lookup = new Map(userOptions.map((u) => [u.id, u.name]));
    return selectedUserIds.map((id) => ({
      id,
      label: lookup.get(id) ?? `User ${id}`,
    }));
  }, [selectedUserIds, userOptions]);

  return (
    <div className="rp-ts-filterbar">
      <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
        {/* Resource picker dropdown */}
        <div className="rp-resource-selection-dropdown">
          <Dialog
            zIndex={1000}
            content={
              <DialogContentContainer
                className="rp-resource-selection-dropdown__content"
                size={DialogContentContainer.sizes.MEDIUM}
              >
                {/* Search */}
                <div className="rp-resource-selection-dropdown__search">
                  <Search
                    size="small"
                    value={searchValue}
                    onChange={(value: string) => setSearchValue(value)}
                    placeholder="Search person or team name"
                  />
                </div>

                <Divider />

                <div className="rp-resource-selection-dropdown__list">
                  {/* Teams section */}
                  {filteredTeams.length > 0 && (
                    <>
                      <Text
                        type={Text.types.TEXT2}
                        className="rp-resource-selection-dropdown__section-title"
                      >
                        Teams
                      </Text>
                      {filteredTeams.map((team) => (
                        <label
                          key={`team-${team.id}`}
                          className="rp-resource-selection-dropdown__option"
                        >
                          <Checkbox
                            checked={selectedTeamIds.includes(team.id)}
                            onChange={() => toggleTeam(team.id)}
                          />
                          <Icon
                            icon={TeamIcon}
                            iconSize={18}
                            className="rp-resource-selection-dropdown__option-icon"
                          />
                          <div className="rp-resource-selection-dropdown__option-info">
                            <Text type={Text.types.TEXT2}>{team.name}</Text>
                            {team.memberNames && team.memberNames.length > 0 && (
                              <Text
                                type={Text.types.TEXT2}
                                className="rp-resource-selection-dropdown__option-desc"
                              >
                                {team.memberNames.join(', ')}
                              </Text>
                            )}
                          </div>
                        </label>
                      ))}
                    </>
                  )}

                  {/* Divider between teams and users */}
                  {filteredTeams.length > 0 && filteredUsers.length > 0 && (
                    <Divider />
                  )}

                  {/* Users section */}
                  {filteredUsers.length > 0 && (
                    <>
                      <Text
                        type={Text.types.TEXT2}
                        className="rp-resource-selection-dropdown__section-title"
                      >
                        Users
                      </Text>
                      {filteredUsers.map((user) => (
                        <label
                          key={`user-${user.id}`}
                          className="rp-resource-selection-dropdown__option"
                        >
                          <Checkbox
                            checked={selectedUserIds.includes(user.id)}
                            onChange={() => toggleUser(user.id)}
                          />
                          {user.avatarUrl ? (
                            <Avatar
                              type="img"
                              src={user.avatarUrl}
                              size="small"
                              ariaLabel={user.name}
                            />
                          ) : (
                            <Avatar
                              type="text"
                              text={user.name}
                              size="small"
                              ariaLabel={user.name}
                            />
                          )}
                          <Text type={Text.types.TEXT2}>{user.name}</Text>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </DialogContentContainer>
            }
            hideTrigger={['clickoutside', 'esckey']}
            showTrigger={['click']}
            position="bottom-start"
          >
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              className="rp-resource-selection-dropdown__trigger"
            >
              <Icon
                icon={selectedTeamIds.length > 0 ? TeamIcon : Person}
                iconSize={16}
                className="rp-resource-selection-dropdown__trigger-icon"
              />
              <span>{triggerLabel}</span>
              <Icon
                icon={DropdownChevronDown}
                iconSize={16}
                className="rp-resource-selection-dropdown__chevron"
              />
            </Button>
          </Dialog>
        </div>

        {/* Board picker dropdown */}
        <div className="rp-resource-selection-dropdown">
          <Dialog
            zIndex={1000}
            content={
              <DialogContentContainer
                className="rp-resource-selection-dropdown__content"
                size={DialogContentContainer.sizes.MEDIUM}
              >
                {/* Search */}
                <div className="rp-resource-selection-dropdown__search">
                  <Search
                    size="small"
                    value={boardSearchValue}
                    onChange={(value: string) => setBoardSearchValue(value)}
                    placeholder="Search boards"
                  />
                </div>

                <Divider />

                <div className="rp-resource-selection-dropdown__list">
                  {/* Loading */}
                  {(isBoardSearching || isLoadingDefaultBoards) && (
                    <div className="rp-resource-selection-dropdown__empty">
                      <Loader size={Loader.sizes.XS} />
                    </div>
                  )}

                  {/* Hint */}
                  {!isBoardSearching &&
                    !isLoadingDefaultBoards &&
                    !isBoardSearchMode &&
                    defaultBoards.length > 0 && (
                      <Text
                        type={Text.types.TEXT2}
                        className="rp-resource-selection-dropdown__section-title"
                        style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}
                      >
                        Recently used · type 2+ characters to search
                      </Text>
                    )}

                  {/* No results */}
                  {!isBoardSearching &&
                    !isLoadingDefaultBoards &&
                    isBoardSearchMode &&
                    boardDisplayItems.length === 0 && (
                      <div className="rp-resource-selection-dropdown__empty">
                        <Text type={Text.types.TEXT2}>No boards found</Text>
                      </div>
                    )}

                  {/* Selected boards */}
                  {selectedBoards.length > 0 && (
                    <>
                      <Text
                        type={Text.types.TEXT2}
                        className="rp-resource-selection-dropdown__section-title"
                      >
                        Selected
                      </Text>
                      {selectedBoards.map((board) => (
                        <label
                          key={`board-sel-${board.id}`}
                          className="rp-resource-selection-dropdown__option"
                        >
                          <Checkbox
                            checked
                            onChange={() => toggleBoard(board)}
                          />
                          <Icon
                            icon={BoardIcon}
                            iconSize={18}
                            className="rp-resource-selection-dropdown__option-icon"
                          />
                          <Text type={Text.types.TEXT2}>{board.name}</Text>
                        </label>
                      ))}
                      {boardDisplayItems.length > 0 && <Divider />}
                    </>
                  )}

                  {/* Board options */}
                  {boardDisplayItems.map((board) => (
                    <label
                      key={`board-${board.id}`}
                      className="rp-resource-selection-dropdown__option"
                    >
                      <Checkbox
                        checked={false}
                        onChange={() => toggleBoard(board)}
                      />
                      <Icon
                        icon={BoardIcon}
                        iconSize={18}
                        className="rp-resource-selection-dropdown__option-icon"
                      />
                      <Text type={Text.types.TEXT2}>{board.name}</Text>
                    </label>
                  ))}
                </div>
              </DialogContentContainer>
            }
            hideTrigger={['clickoutside', 'esckey']}
            showTrigger={['click']}
            position="bottom-start"
            onDialogDidShow={loadDefaultBoards}
          >
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              className="rp-resource-selection-dropdown__trigger"
            >
              <Icon
                icon={BoardIcon}
                iconSize={16}
                className="rp-resource-selection-dropdown__trigger-icon"
              />
              <span>
                {selectedBoards.length === 0
                  ? 'All boards'
                  : selectedBoards.length === 1
                    ? selectedBoards[0].name
                    : `${selectedBoards.length} boards`}
              </span>
              <Icon
                icon={DropdownChevronDown}
                iconSize={16}
                className="rp-resource-selection-dropdown__chevron"
              />
            </Button>
          </Dialog>
        </div>

        {/* Active filter chips */}
        {totalActive > 0 && (
          <>
            <Divider direction={Divider.directions.VERTICAL} />

            <Flex
              gap={Flex.gaps.XS}
              align={Flex.align.CENTER}
              className="rp-ts-filterbar__chips"
            >
              {teamChips.map((chip) => (
                <span key={`team-${chip.id}`} className="rp-filter-chip">
                  <Text type={Text.types.TEXT2} className="rp-filter-chip__label">
                    {chip.label}
                  </Text>
                  <button
                    type="button"
                    className="rp-filter-chip__remove"
                    onClick={() =>
                      onTeamSelectionChange(
                        selectedTeamIds.filter((t) => t !== chip.id),
                      )
                    }
                    aria-label={`Remove filter: ${chip.label}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
              {userChips.map((chip) => (
                <span key={`user-${chip.id}`} className="rp-filter-chip">
                  <Text type={Text.types.TEXT2} className="rp-filter-chip__label">
                    {chip.label}
                  </Text>
                  <button
                    type="button"
                    className="rp-filter-chip__remove"
                    onClick={() =>
                      onUserSelectionChange(
                        selectedUserIds.filter((u) => u !== chip.id),
                      )
                    }
                    aria-label={`Remove filter: ${chip.label}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
              {selectedBoards.map((board) => (
                <span key={`board-${board.id}`} className="rp-filter-chip">
                  <Text type={Text.types.TEXT2} className="rp-filter-chip__label">
                    {board.name}
                  </Text>
                  <button
                    type="button"
                    className="rp-filter-chip__remove"
                    onClick={() =>
                      onBoardSelectionChange(
                        selectedBoards.filter((b) => b.id !== board.id),
                      )
                    }
                    aria-label={`Remove filter: ${board.name}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </Flex>

            {/* Clear all */}
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              leftIcon={Close}
              onClick={handleClear}
            >
              <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER}>
                <span>Clear</span>
                <Counter
                  count={totalActive}
                  size={Counter.sizes.SMALL}
                  color={Counter.colors.NEGATIVE}
                />
              </Flex>
            </Button>
          </>
        )}
      </Flex>
    </div>
  );
};

export default TimesheetFilterBar;
